import {
  Box,
  Button, 
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  FormControl, 
  FormErrorMessage, 
  FormLabel,
  Heading, 
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Icon,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from "yup";
import { FiMail, FiSend, FiLogIn, FiCheck } from 'react-icons/fi';
import { sendPasswordResetEmail } from "../services/user-service";
import { LOGIN_PAGE_ROUTE, LINK_COLOR } from '../constants';
import ColorModeToggle from '../components/color-mode-toggle';

function ForgotPassword() {
  const [showMessage, setShowMessage] = useState(false);
  const [requestCompleted, setRequestCompleted] = useState(false);


  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('green.700', 'green.300');
  const successCardBorder = useColorModeValue('green.100', 'gray.700');
  const successCardHeaderBg = useColorModeValue('green.500', 'green.600');
  const greenCardBorder = useColorModeValue('green.100', 'gray.700');
  const greenCardHeaderBg = useColorModeValue('green.500', 'green.600');
  const bgGradientOverlay = useColorModeValue(
    'linear(135deg, rgba(247, 250, 252, 0.85) 0%, rgba(240, 253, 244, 0.85) 100%)',
    'linear(135deg, rgba(26, 32, 44, 0.85) 0%, rgba(45, 55, 72, 0.85) 100%)'
  );

  return (
    <Box
      minH="100vh"
      position="relative"
      py={8}
      className="fade-in-up"
      backgroundImage="url('/pacifico-sede.gif')"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgGradient: bgGradientOverlay,
        zIndex: 1
      }}
    >
      <Container maxW="lg" centerContent position="relative" zIndex={2}>
        <Flex
          direction="column"
          align="center"
          w="full"
          mb={8}
        >
          <HStack justify="space-between" w="full" mb={4}>
            <Box />
            <ColorModeToggle />
          </HStack>

          {requestCompleted && showMessage ? (
            <Card
              w="full"
              maxW="md"
              shadow="2xl"
              bg={cardBg}
              borderRadius="2xl"
              border="1px"
              borderColor={successCardBorder}
              className="card-hover"
              overflow="hidden"
            >
              <CardHeader
                bg={successCardHeaderBg}
                color="white"
                textAlign="center"
                py={6}
              >
                <VStack spacing={2}>
                  <Icon as={FiCheck} boxSize={10} />
                  <Heading size="lg" fontWeight="bold">
                    ¡Correo enviado!
                  </Heading>
                  <Text fontSize="sm" opacity={0.9}>
                    Revisá tu bandeja de entrada
                  </Text>
                </VStack>
              </CardHeader>
              <CardBody p={8} textAlign="center">
                <VStack spacing={4}>
                  <Text color={textColor}>
                    Se ha enviado un correo electrónico para restablecer la contraseña de la cuenta.
                  </Text>
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription color="blue.700">
                      ¡Recordá que el enlace dura una hora!
                    </AlertDescription>
                  </Alert>
                  <Button
                    as={RouterLink}
                    to={LOGIN_PAGE_ROUTE}
                    colorScheme="green"
                    leftIcon={<FiLogIn />}
                    _hover={{
                      transform: 'translateY(-1px)',
                      boxShadow: 'md'
                    }}
                  >
                    Volver al inicio de sesión
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            <Card
              w="full"
              maxW="md"
              shadow="2xl"
              bg={cardBg}
              borderRadius="2xl"
                             border="1px"
               borderColor={greenCardBorder}
               className="card-hover"
              overflow="hidden"
            >
                             <CardHeader
                 bg={greenCardHeaderBg}
                 color="white"
                textAlign="center"
                py={6}
              >
                <VStack spacing={2}>
                  <Icon as={FiMail} boxSize={8} />
                  <Heading size="lg" fontWeight="bold">
                    Recuperar contraseña
                  </Heading>
                  <Text fontSize="sm" opacity={0.9}>
                    Te ayudamos a restablecer tu acceso
                  </Text>
                </VStack>
              </CardHeader>

              <CardBody p={8}>
                <VStack spacing={6} mb={6}>
                  <Text color={textColor} textAlign="center">
                    ¿Te olvidaste la contraseña para acceder a la sede virtual? ¡No te preocupes! A todos nos pasó alguna vez 😉
                  </Text>
                  <Alert status="info" borderRadius="lg">
                    <AlertIcon />
                    <VStack align="start" spacing={2} fontSize="sm">
                      <Text fontWeight="semibold" color="blue.700">
                        ¿Cómo funciona?
                      </Text>
                      <Text color="blue.600">
                        1. Escribí tu correo electrónico
                      </Text>
                      <Text color="blue.600">
                        2. Te enviaremos un enlace de recuperación
                      </Text>
                      <Text color="blue.600">
                        3. Usá el enlace para crear una nueva contraseña
                      </Text>
                      <Text color="blue.600" fontSize="xs" fontStyle="italic">
                        * El enlace dura una hora
                      </Text>
                    </VStack>
                  </Alert>
                </VStack>

                <Formik
                  initialValues={{
                    email: ''
                  }}
                  validationSchema={
                    Yup.object({
                      email: Yup.string()
                        .email('Ingresá una dirección de correo electrónico válida.')
                        .required('Necesitamos saber tu dirección de correo electrónico.'),
                    })
                  }
                  onSubmit={(values, { setSubmitting }) => {
                    setTimeout(async () => {
                      const response = await sendPasswordResetEmail(values.email);
                      if (response) {
                        setRequestCompleted(true);
                        setShowMessage(true);
                      }
                      setSubmitting(false);
                    }, 400);
                  }}
                >
                  {
                    formik => (
                      <Form style={{ width: '100%' }}>
                        <Stack spacing={6}>
                          <FormControl
                            isInvalid={
                              (formik.touched.email ?? false) &&
                              formik.errors.email != null
                            }
                          >
                            <FormLabel
                              htmlFor="email"
                              color={textColor}
                              fontWeight="semibold"
                            >
                              Correo electrónico
                            </FormLabel>
                            <InputGroup>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="tu@email.com"
                                size="lg"
                                borderRadius="lg"
                                focusBorderColor="green.500"
                                _hover={{
                                  borderColor: 'green.300'
                                }}
                              />
                              <InputRightElement pointerEvents="none" height="3rem">
                                <Icon as={FiMail} color="gray.400" />
                              </InputRightElement>
                            </InputGroup>
                            <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                          </FormControl>

                          <Button
                            type="submit"
                            colorScheme="green"
                            size="lg"
                            borderRadius="lg"
                            isLoading={formik.isSubmitting}
                            loadingText="Enviando..."
                            leftIcon={<FiSend />}
                            disabled={requestCompleted}
                            _hover={{
                              transform: 'translateY(-2px)',
                              boxShadow: 'lg'
                            }}
                          >
                            Enviar enlace de recuperación
                          </Button>
                        </Stack>
                      </Form>
                    )
                  }
                </Formik>
              </CardBody>
            </Card>
          )}

          <VStack spacing={4} mt={8} textAlign="center">
            <Divider />
            
            <VStack spacing={2}>
              <Text color={headingColor} fontWeight="semibold" fontSize="lg">
                ¿Recordaste tu contraseña?
              </Text>
              <Text color={textColor}>
                Podés volver a la página de inicio de sesión
              </Text>
              <Button
                as={RouterLink}
                to={LOGIN_PAGE_ROUTE}
                variant="outline"
                colorScheme="green"
                size="md"
                leftIcon={<FiLogIn />}
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'md'
                }}
              >
                Volver al inicio de sesión
              </Button>
            </VStack>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
}

export default ForgotPassword;
